import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { AtAGlanceOutput, UseAtAGlanceOptions } from './atAGlanceTypes.ts';
import { atAGlanceResultCache } from './atAGlanceResultCache.ts';
import { useAtAGlanceRequestBody } from './useAtAGlanceRequestBody.ts';
import { createAtAGlanceRequestReporter } from './createAtAGlanceRequestReporter.ts';
import { type AtAGlanceRetryReason, clearQuotaCooldownsForTesting } from './atAGlanceRetry.ts';
import { startAtAGlanceRequest } from './atAGlanceRequest.ts';

type AtAGlanceResultState = {
	contentId: string;
	output: AtAGlanceOutput | null;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

export const clearAtAGlanceResultCacheForTesting = (): void => {
	atAGlanceResultCache.clear();
	clearQuotaCooldownsForTesting();
};

export type UseAtAGlanceResult = {
	output: AtAGlanceOutput | null;
	/**
	 * True while we are still waiting on prerequisites or the cc-smarts response.
	 * False once we have output, have an error, or know we will not fetch (ineligible).
	 */
	loading: boolean;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

/**
 * Asks cc-smarts for the Teamwork Graph "at a glance" overview and per-link
 * summaries. When enabled, waits for Appears in, Links to, and enabled Related
 * content to settle before the first request.
 *
 * The byline enables requests on the first panel open for the current page. Its
 * dedicated bridge shares the result with the menu, panel and modal. Closing or
 * reopening those surfaces does not cancel or restart work.
 *
 * Failures are reported and returned as `error` — the rest of the connections UI
 * must not depend on this response. Overview bullets are rendered by
 * `AtAGlanceSection`; the per-link one-liners are rendered on Related content
 * rows.
 *
 * Each logical request reports one terminal operational event —
 * succeeded, failed (with a categorised reason), or aborted (torn down in flight).
 * A cache hit fires nothing: no request was made.
 */
export const useAtAGlance = (options: UseAtAGlanceOptions): UseAtAGlanceResult => {
	const { enabled, contentId, connections } = options;
	const { cloudId } = useSessionData();
	const { locale } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const [resultState, setResultState] = useState<AtAGlanceResultState | null>(null);
	const { requestBody, waitingOnPrereqs } = useAtAGlanceRequestBody(options, { cloudId, locale });
	const cachedOutput = requestBody ? (atAGlanceResultCache.get(requestBody) ?? null) : null;
	const currentResultState = resultState?.contentId === contentId ? resultState : null;
	const output = cachedOutput ?? currentResultState?.output ?? null;
	const error = cachedOutput ? null : (currentResultState?.error ?? null);

	// Raw link counts can change without changing the request body. Snapshot them when
	// the effect starts so analytics counts never restart requests.
	const linkCountsRef = useRef({ incomingLinkCount: 0, outgoingLinkCount: 0 });
	linkCountsRef.current = {
		incomingLinkCount: connections.appearsIn.links.length,
		outgoingLinkCount: connections.linksTo.links.length,
	};

	useEffect(() => {
		if (!requestBody || !cloudId || !contentId) {
			setResultState((current) =>
				current
					? {
							contentId: current.contentId,
							output: current.output,
							error: current.error,
						}
					: null,
			);
			return;
		}
		const cachedResult = atAGlanceResultCache.get(requestBody);
		if (cachedResult) {
			// Keep this mounted consumer stable if another page later replaces the
			// single module-level cache entry. A cache hit is not a request, so no
			// operational event fires here.
			setResultState({ contentId, output: cachedResult, error: null });
			return;
		}

		const reporter = createAtAGlanceRequestReporter(createEvent, linkCountsRef.current);
		// Clearing output here would replace both surfaces' existing summaries with skeletons.
		setResultState((current) => ({
			contentId,
			output: current && current.contentId === contentId ? current.output : null,
			error: null,
		}));

		return startAtAGlanceRequest(
			{ cloudId, requestBody },
			{ fetch },
			{
				onSuccess: (parsed, attributes) => {
					atAGlanceResultCache.set(requestBody, parsed);
					setResultState({ contentId, output: parsed, error: null });
					reporter.onSuccess(parsed, attributes);
				},
				onFailure: (failure, attributes) => {
					setResultState((current) => ({
						contentId,
						output: current && current.contentId === contentId ? current.output : null,
						error: failure,
					}));
					reporter.onFailure(failure, attributes);
				},
				onRetryState: (retryPending, retryReason) => {
					setResultState((current) => ({
						contentId,
						output: current?.contentId === contentId ? current.output : null,
						error: retryPending ? null : (current?.error ?? null),
						retryPending,
						retryReason,
					}));
				},
				onAborted: reporter.onAborted,
			},
		);
	}, [cloudId, contentId, createEvent, requestBody]);

	const loading = Boolean(enabled && !output && !error && (waitingOnPrereqs || requestBody));
	const retryPending = cachedOutput ? false : currentResultState?.retryPending;
	const retryReason = retryPending ? currentResultState?.retryReason : undefined;
	return useMemo(
		() => ({ output, error, loading, retryPending, retryReason }),
		[output, error, loading, retryPending, retryReason],
	);
};
